import { Box, Paper, PaperProps, Popper, styled } from "@mui/material";

export const ARROW_SIZE = 12;
// Shared between the popper (which positions the arrow per placement) and the arrow
// element itself, so the two stay in sync.
export const ARROW_CLASS = "HoverCard-arrow";
/**
 * Popper styled so the arrow sits on the correct edge for whichever side
 * popper.js flips to. Content is interactive (the pointer can move into it).
 */
export const TooltipPopper = styled(Popper)(({ theme }) => ({
  zIndex: theme.zIndex.tooltip,
  // Anchor the clip window so its inner edge lands exactly on the card edge,
  // then push the rotated square so only its outer (visible) half shows.
  [`&[data-popper-placement*="bottom"] .${ARROW_CLASS}`]: {
    top: 0,
    marginTop: -ARROW_SIZE * 2,
    "&::before": { top: `calc(100% - ${ARROW_SIZE / 2}px)` },
  },
  [`&[data-popper-placement*="top"] .${ARROW_CLASS}`]: {
    bottom: 0,
    marginBottom: -ARROW_SIZE * 2,
    "&::before": { top: -ARROW_SIZE / 2 },
  },
}));
/**
 * Clip window holding the rotated square that forms the popper's arrow. popper.js owns
 * this element's `transform` (it centers the arrow on the anchor); `overflow: hidden`
 * keeps only the outer half of the square (and its shadow) visible, hiding the
 * inward-cast shadow that would otherwise streak across the card. The square's rotation
 * lives here; its cross-axis offset is set per placement in TooltipPopper.
 */
export const TooltipArrow = styled(Box)(({ theme }) => ({
  height: ARROW_SIZE * 2,
  overflow: "hidden",
  position: "absolute",
  width: ARROW_SIZE * 3,
  // Decorative only. Its box overlaps the trigger's edge (it is pulled `ARROW_SIZE * 2`
  // up while the offset gap is only `ARROW_SIZE`), so if it captured the pointer it would
  // sit on top of the hovered trigger on open, steal its hover, close the card, and
  // oscillate. Letting the pointer pass through keeps the trigger hovered underneath.
  pointerEvents: "none",
  "&::before": {
    backgroundColor: theme.palette.background.paper,
    boxShadow: "var(--mui-shadows-4)",
    content: '""',
    height: ARROW_SIZE,
    left: "100%",
    marginLeft: -ARROW_SIZE / 2,
    position: "absolute",
    transform: "translateX(-100%) rotate(45deg)",
    width: ARROW_SIZE,
  },
}));
/**
 * Card surface of the hover tooltip. It sizes to its content (`max-content`) rather than to a
 * fixed column, so rows like the stats strip or a long e-mail decide the width instead of being
 * wrapped into a narrow card. The cap keeps a wide card from running off-screen and matches the
 * 8px `preventOverflow` padding the popper is configured with.
 */
export const TooltipPaper = styled(Paper)<PaperProps>(({ theme }) => ({
  borderRadius: 2,
  boxShadow: theme.shadows[4],
  maxWidth: "min(480px, calc(100vw - 16px))",
  overflow: "visible",
  padding: theme.spacing(2),
  position: "relative",
  width: "max-content",
}));
